import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { LuFileMinus } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";

import ListPageShell from "../../list-page/ListPageShell";
import ListPageHeader from "../../list-page/ListPageHeader";
import ListPageSearch from "../../list-page/ListPageSearch";
import ListPageEmpty from "../../list-page/ListPageEmpty";
import ListPager from "../../list-page/ListPager";

import ReceiptTable from "../ReceiptTable";
import ReactModal from "../../ReactModal";
import ReceiptView from "../../ReceiptView";
import { useGetNonFiscalReceiptsQuery } from "../../../store/services/receiptsServices";
import { receiptViewActions } from "../../../store/receiptViewSlice";

const PAGE_LIMIT = 10;

export const NonFiscalReceipts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isReceiptViewOpen = useSelector((s) => s.receipt_view.isOpen);

  const [filteredNonFiscalReceipts, setFilteredNonFiscalReceipts] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));

  const { data, isLoading, isFetching } = useGetNonFiscalReceiptsQuery({ params });

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredNonFiscalReceipts(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const urlParams = new URLSearchParams(params).toString();
    navigate(`/non_fiscal_receipts?${urlParams}`, { replace: true });
  }, [params, navigate]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (receipt) => {
    dispatch(receiptViewActions.openModal(receipt));
  };

  const hasFilters = !!params.q;

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty =
    !isLoading && !isFetching && filteredNonFiscalReceipts.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      {isReceiptViewOpen && <ReceiptView />}
      <ReactModal />

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuFileMinus size={18} />, label: "Kuponat jo fiskalë" }}
          title="Kuponat jo fiskalë"
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} kupona jo fiskalë`
              : "Lista e kuponave jo fiskalë"
          }
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas ID-së ose përdoruesit..."
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : LuFileMinus}
            title={hasFilters ? "Asnjë kupon nuk u gjet" : "Ende asnjë kupon jo fiskal"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : "Kuponat jo fiskalë do të shfaqen këtu."
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <ReceiptTable
            receipts={filteredNonFiscalReceipts}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
};
